// provider.ts — cliente del marketplace del PROVEEDOR self-service (S3 P1).
//
// El proveedor NO es staff de clínica ni tutor: tiene su propia identidad
// (marketplace_provider_users) con JWT propio (ProviderClaims{Kind:"provider"}).
// Por eso usa su propio token en sessionStorage bajo una clave SEPARADA
// (wiservet:provider_access_token) y su propia instancia axios con su propio
// interceptor — nunca comparte storage ni interceptor con el staff
// (wiservet:access_token, stores/auth.ts) ni con el tutor
// (wiservet:tutor_access_token, services/tutor.ts). Mezclar sesiones haría que
// un logout de un actor afecte al otro, o que un token de un actor se cuele en
// requests del otro (el backend ya rechaza esto por Kind+audiencia, pero acá
// evitamos el problema antes de llegar al backend).
import axios from 'axios';

const PROVIDER_TOKEN_KEY = 'wiservet:provider_access_token';

const providerApi = axios.create({
  baseURL: '/api/v1/provider',
  withCredentials: false,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

providerApi.interceptors.request.use((config) => {
  const token = sessionStorage.getItem(PROVIDER_TOKEN_KEY);
  if (token && config.headers) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Sesión revocada (Changelog v2 #2: session_version cambia o status!=active) o
// token simplemente inválido/expirado → limpiar y avisar a la UI (la vista
// escucha este evento para redirigir a /proveedor/ingresar con un mensaje).
providerApi.interceptors.response.use(
  (resp) => resp,
  (error) => {
    if (error?.response?.status === 401) {
      sessionStorage.removeItem(PROVIDER_TOKEN_KEY);
      window.dispatchEvent(new CustomEvent('wiservet:provider-session-expired'));
    }
    return Promise.reject(error);
  },
);

export function getProviderToken(): string | null {
  return sessionStorage.getItem(PROVIDER_TOKEN_KEY);
}

export function setProviderToken(token: string | null): void {
  if (token) sessionStorage.setItem(PROVIDER_TOKEN_KEY, token);
  else sessionStorage.removeItem(PROVIDER_TOKEN_KEY);
}

export interface ProviderApiError {
  code: string;
  message: string;
  status: number;
}

export function asProviderApiError(err: unknown): ProviderApiError {
  const ax = err as { response?: { status?: number; data?: { code?: string; error?: string } }; message?: string };
  const status = ax.response?.status ?? 0;
  const code = ax.response?.data?.code ?? 'NETWORK_ERROR';
  const message = ax.response?.data?.error ?? ax.message ?? 'Error de red';
  return { code, message, status };
}

// --- Contratos (espejo de backend/internal/handler/marketplace_provider_handler.go) ---

export interface ProviderSession {
  access_token: string;
  token_type: string;
  expires_in: number;
  provider_id: string;
  role: string;
}

export interface ProviderMe {
  provider_user_id: string;
  email: string;
  role: string;
  status: string;
  last_login_at?: string | null;
  provider: {
    id: string;
    name: string;
    category: string;
  };
}

// ProviderOrderView — proyección minimizada (Changelog v2 #4): NUNCA
// email/teléfono/RUT del tutor ni clinic_amount/wiservet_amount.
export interface ProviderOrderView {
  id: string;
  created_at: string;
  status: string;
  order_kind: string;
  price: number;
  currency: string;
  tutor_first_name: string;
  tutor_last_initial: string;
  item_name: string;
  slot_starts_at?: string | null;
}

export interface ProviderSlot {
  id: string;
  service_id: string;
  provider_id: string;
  starts_at: string;
  duration_min?: number | null;
  status: string;
  order_id?: string | null;
}

export interface ProviderPayoutView {
  order_id: string;
  order_kind: string;
  item_name: string;
  gross: number;
  commission: number;
  net: number;
  status: string;
  paid_at?: string | null;
  created_at: string;
}

// ProviderServiceView — "mis servicios" (GET /provider/services), usado por
// el selector de servicio en Publicar cupo / Bloquear día. Reemplaza el
// campo de UUID manual: el proveedor elige por NOMBRE.
export interface ProviderServiceView {
  id: string;
  name: string;
  category: string;
  price: number;
  duration_min?: number | null;
  status: string;
}

export const providerService = {
  async activate(token: string, password: string): Promise<ProviderSession> {
    const resp = await providerApi.post<ProviderSession>('/activate', { token, password });
    setProviderToken(resp.data.access_token);
    return resp.data;
  },
  async login(email: string, password: string): Promise<ProviderSession> {
    const resp = await providerApi.post<ProviderSession>('/login', { email, password });
    setProviderToken(resp.data.access_token);
    return resp.data;
  },
  logout(): void {
    setProviderToken(null);
  },
  async me(): Promise<ProviderMe> {
    const resp = await providerApi.get<ProviderMe>('/me');
    return resp.data;
  },
  async listServices(): Promise<ProviderServiceView[]> {
    const resp = await providerApi.get<{ services: ProviderServiceView[] }>('/services');
    return resp.data.services ?? [];
  },
  async listOrders(status?: string, page = 1, pageSize = 20): Promise<{ orders: ProviderOrderView[]; total: number }> {
    const resp = await providerApi.get<{ orders: ProviderOrderView[]; total: number; page: number; page_size: number }>('/orders', {
      params: { status: status || undefined, page, page_size: pageSize },
    });
    return { orders: resp.data.orders ?? [], total: resp.data.total ?? 0 };
  },
  async confirmOrder(id: string): Promise<{ id: string; status: string }> {
    const resp = await providerApi.post<{ id: string; status: string }>(`/orders/${id}/confirm`, {});
    return resp.data;
  },
  async rejectOrder(id: string): Promise<{ id: string; status: string }> {
    const resp = await providerApi.post<{ id: string; status: string }>(`/orders/${id}/reject`, {});
    return resp.data;
  },
  async fulfillOrder(id: string): Promise<{ id: string; status: string }> {
    const resp = await providerApi.post<{ id: string; status: string }>(`/orders/${id}/fulfill`, {});
    return resp.data;
  },
  async listSlots(serviceId?: string, availableOnly = false): Promise<ProviderSlot[]> {
    const resp = await providerApi.get<{ slots: ProviderSlot[] }>('/slots', {
      params: { service_id: serviceId || undefined, available: availableOnly ? 'true' : undefined },
    });
    return resp.data.slots ?? [];
  },
  async publishSlot(serviceId: string, startsAt: string, durationMin?: number): Promise<ProviderSlot> {
    const resp = await providerApi.post<ProviderSlot>('/slots', {
      service_id: serviceId,
      starts_at: startsAt,
      duration_min: durationMin,
    });
    return resp.data;
  },
  async blockDay(serviceId: string, date: string): Promise<{ blocked_slots: number; date: string }> {
    const resp = await providerApi.post<{ blocked_slots: number; date: string }>('/slots/block-day', {
      service_id: serviceId,
      date,
    });
    return resp.data;
  },
  async listPayouts(status?: string): Promise<ProviderPayoutView[]> {
    const resp = await providerApi.get<{ payouts: ProviderPayoutView[] }>('/payouts', {
      params: { status: status || undefined },
    });
    return resp.data.payouts ?? [];
  },
};
